import { createSelector } from 'reselect';
import { Selector } from '../../rootReducer';
import { RequestStatus } from '../../types';
import { IImportSource, IMediaImportState } from './reducer';
import { IImportedMediaItem, MediaImportStatus } from './types';

export const getImportedMedia: Selector<IMediaImportState> = (state) =>
  state.media.imported;

const getSelection: Selector<string[]> = createSelector(
  getImportedMedia,
  (importedMedia) => importedMedia.selection,
);

const getImportedSiteData: Selector<IImportSource<IImportedMediaItem[]>> =
  createSelector(getImportedMedia, (importedMedia) => importedMedia.site);

const getImportedFacebookData: Selector<IImportSource<IImportedMediaItem[]>> =
  createSelector(getImportedMedia, (importedMedia) => importedMedia.facebook);

const getImportedSiteMedia: Selector<IImportedMediaItem[]> = createSelector(
  getImportedSiteData,
  getSelection,
  (siteMedia, selection) =>
    (siteMedia.items || []).map((item) => ({
      ...item,
      selected: selection.includes(item.name),
    })),
);

const getImportedFacebookMedia: Selector<IImportedMediaItem[]> = createSelector(
  getImportedFacebookData,
  getSelection,
  (facebookMedia, selection) =>
    (facebookMedia.items || []).map((item) => ({
      ...item,
      selected: selection.includes(item.name),
    })),
);

const getAllImportedMedia: Selector<IImportedMediaItem[]> = createSelector(
  getImportedFacebookMedia,
  getImportedSiteMedia,
  (facebookMedia, siteMedia) => [...facebookMedia, ...siteMedia],
);

const getFacebookUrl: Selector<string> = createSelector(
  getImportedMedia,
  (importedMedia) => importedMedia.facebook.url || '',
);

const getSiteUrl: Selector<string> = createSelector(
  getImportedMedia,
  (importedMedia) => importedMedia.site.url || '',
);

const isMediaLoading: Selector<boolean> = createSelector(
  getImportedSiteData,
  getImportedFacebookData,
  (siteMedia, facebookMedia) =>
    siteMedia.status === RequestStatus.REQUEST ||
    facebookMedia.status === RequestStatus.REQUEST,
);

const isFacebookMediaEmpty: Selector<boolean> = createSelector(
  getImportedFacebookData,
  (facebookMedia) =>
    facebookMedia.status === RequestStatus.SUCCESS &&
    (!facebookMedia.items || facebookMedia.items.length === 0),
);

const isSiteMediaEmpty: Selector<boolean> = createSelector(
  getImportedSiteData,
  (siteMedia) =>
    siteMedia.status === RequestStatus.SUCCESS &&
    (!siteMedia.items || siteMedia.items.length === 0),
);

const isShowImportNotification: Selector<boolean> = createSelector(
  getImportedMedia,
  (siteMedia) => siteMedia.showImportNotification,
);

const mediaImportStatus: Selector<MediaImportStatus> = createSelector(
  isFacebookMediaEmpty,
  isSiteMediaEmpty,
  (isFacebookEmpty, isSiteEmpty) => {
    if (isFacebookEmpty && isSiteEmpty) {
      return MediaImportStatus.NO_ANY_MEDIA;
    }
    if (isSiteEmpty) {
      return MediaImportStatus.NO_SITE_MEDIA;
    }
    if (isFacebookEmpty) {
      return MediaImportStatus.NO_FACEBOOK_MEDIA;
    }

    return MediaImportStatus.HAS_ANY_MEDIA;
  },
);

export {
  getAllImportedMedia,
  getImportedFacebookMedia,
  getImportedSiteMedia,
  getFacebookUrl,
  getSiteUrl,
  isMediaLoading,
  isFacebookMediaEmpty,
  isSiteMediaEmpty,
  isShowImportNotification,
  mediaImportStatus,
};
